import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';
import { Brand, BrandFilterOption, Company, CompanyFilterOption, ConfirmRequestedProductsImportRequest, FileParameter, FileVaultCategory, FileVaultReference, GetListRequestedProductsFiltersRequest, GetListRequestedProductsFiltersResponse, ImportFileFilterOption, ImportRequestedProductsResponse, ListBrandsRequest, ListRangesRequest, ListRequestedProductsRequest, ListRequestedProductsRequestOrderField, ListRequestedProductsResponse, ProductClassificationFilterOption, ProductStateFilterOption, ProductType, ProductTypeFilterOption, ProductWorkflow, ProductWorkflowState, QualityLevel, Range, RequestedProduct, RequestedProductFileVaultReference, RequestedProductRange, SituViewWebApiClient, WorkflowStateType } from 'situview-webapi-client';
import { FileVaultSelectorDialogComponent, FileVaultSelectorDialogData, FileVaultSelectorResultData, PreWorkflowTriggerStep, ProductSelectorDialogComponent, ProductSelectorDialogData, ProductSelectorResultData, WorkflowTriggerDialogComponent, WorkflowTriggerResultData } from 'src/app/components';
import { AuthService, BannerService, BannerServiceStatus, BreadcrumbService, CompanyViewService, ProductService, StateService, Utils } from 'src/app/services';
import { RangeService } from 'src/app/services/range.service';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { CreateBrandDialogComponent } from '../create-brand-dialog/create-brand-dialog/create-brand-dialog.component';
import { Sort } from '@angular/material/sort';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'requested-products-list',
  templateUrl: './requested-products-list.component.html',
  styleUrls: ['./requested-products-list.component.less'],
})
export class RequestedProductsListComponent extends SituBaseComponent {

  private readonly defaultColumns = ['select', 'supplier', 'name', 'productCode', 'productType', 'description', 'brand', 'qualityLevel', 'ranges', 'fileVaultFiles', 'state', 'notes', 'edit'];
  public readonly QualityLevel = QualityLevel;
  public readonly Number = Number;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  pageNumber = 1;
  pageSize = 100;
  totalItems = 0;

  sortBy = ListRequestedProductsRequestOrderField.Unset;
  sortDescending = false;

  requestedProducts: RequestedProductLineItem[] = [];

  displayedColumns: string[] = this.defaultColumns;

  reportId = '';
  isReportReady = false;
  reportErrorCount = 0;
  reportPollCount = 0;

  supplierIdFilter: string | undefined;
  supplierNameFilter: string | undefined;
  brandIdFilter: string | undefined;
  brandNameFilter: string | undefined;
  importCompanyIdFilter: string | undefined;
  importCompanyNameFilter: string | undefined;
  importFileIdFilter: string | undefined;
  importFileNameFilter: string | undefined;
  productClassificationIdFilter: string | undefined;
  productClassificationNameFilter: string | undefined;
  productTypeIdFilter: string | undefined;
  productTypeNameFilter: string | undefined;
  productStateFilter: ProductWorkflowState | undefined;
  productStateNameFilter: string | undefined;
  searchTerm: string = "";

  loadingFilterOption = false;

  productWorkflowState = ProductWorkflowState;
  workflowStateType = WorkflowStateType;

  filename: string | undefined;

  workflowTriggerDialogRef: MatDialogRef<WorkflowTriggerDialogComponent> | undefined;
  productSelectorDialogRef: MatDialogRef<ProductSelectorDialogComponent> | undefined;

  @ViewChild('paginator') paginator: MatPaginator | undefined;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private bannerService: BannerService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private productService: ProductService,
    private companyViewService: CompanyViewService,
    private breadcrumbService: BreadcrumbService,
    private rangeService: RangeService,
    private situViewClient: SituViewWebApiClient) {
    super(authService, stateService);
     }

  protected onSelectedCompanyChange(): void {
    this.rangesBySupplier = {};

    Promise.all([
      this.loadSuppliers(),
      this.loadAllBrands(),
      this.loadProductStates(),
      this.loadProductTypes(),
      this.loadData(),
    ]);
  }

  ngOnInit() {
    super.ngOnInit();
    
    this.filteredSuppliers = this.supplierControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterSuppliers(value);
      })
    );
    
    this.filteredBrands = this.brandControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBrands(value);
      })
    );
    
    this.filteredImportCompanies = this.importCompanyControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterImportCompanies(value);
      })
    );
    
    this.filteredImportFiles = this.importFileControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterImportFiles(value);
      })
    );

    this.filteredProductTypes = this.productTypeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    this.filteredProductClassifications = this.productClassificationControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductClassifications(value);
      })
    );

    this.filteredProductStates = this.productStateControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductStates(value);
      })
    );

    this.filteredBulkSuppliers = this.bulkSupplierControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBulkSuppliers(value);
      })
    );

    this.filteredBulkBrands = this.bulkBrandControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBulkBrands(value);
      })
    );

    this.filteredBulkProductTypes = this.bulkProductTypeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBulkProductTypes(value);
      })
    );

    this.filteredBulkRanges = this.bulkRangeControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterRangesForSupplier(value, this.getEditModeCommonSupplierId());
      })
    );
  }

  async onSearch() {
    this.paginator?.firstPage();

    await this.loadData(true);

    var importFile = this.importFileFilterOptions?.find(x => x.id === this.importFileIdFilter);
    this.selectedImportFile = importFile;
  }

  onPageChange(evt: PageEvent) {
    this.pageSize = evt.pageSize;
    this.pageNumber = evt.pageIndex + 1;

    this.loadData(true);
  }

  async loadData(clearRequestedProducts = true) {
    var requestedProductPage = await this.getRequestedProducts()

    if (clearRequestedProducts) {
      this.requestedProducts = requestedProductPage.items!.map(x => new RequestedProductLineItem(x));
    } else {
      this.updateExistingLineItems(requestedProductPage.items!);
    }

    this.totalItems = requestedProductPage.totalItems!;
  }

  onSaveLineItem(item: RequestedProductLineItem) {
    this.saveLineItem(item);
  }

  onSaveAllEditModeLineItems() {
    this.loading = true;

    var requestedProductsToUpdate = this.requestedProducts
      .filter(x => x.editMode)
      .map(x => x.requestedProduct);

    this.productService.UpdateRequestedProducts(requestedProductsToUpdate)
      .then((requestedProducts: RequestedProduct[] | undefined) => {
        if (requestedProducts) {
          requestedProducts.forEach(x => this.updateExistingLineItem(x));
          this.showMessage("The Requested Products have been updated");
        } else {
          this.showMessage("There was an issue updating the requested products.");
        }
      })
      .finally(() => {
        this.loading = false;
        this.clearAllFilterOptions();
      });
  }

  saveLineItem(item: RequestedProductLineItem) {
    this.loading = true;

    this.productService.UpdateRequestedProduct(item.requestedProduct)
      .then((requestedProduct: RequestedProduct | undefined) => {
        if (requestedProduct) {
          this.updateExistingLineItem(requestedProduct);
          this.showMessage("The Requested Product has been updated");
        } else {
          this.showMessage("There was an issue updating the requested product.");
        }
      })
      .finally(() => {
        this.loading = false;
        this.clearAllFilterOptions();
      });
  }

  updateExistingLineItems(updatedRequestedProducts: RequestedProduct[]) {
    updatedRequestedProducts.forEach(x => this.updateExistingLineItem(x));
  }

  updateExistingLineItem(updatedRequestedProduct: RequestedProduct) {
    var existingLineItem = this.requestedProducts.find(y => y.requestedProduct.id === updatedRequestedProduct.id);
    if (existingLineItem) {
      existingLineItem.requestedProduct = updatedRequestedProduct;
      existingLineItem.editMode = false;
    } else {
      this.requestedProducts.push(new RequestedProductLineItem(updatedRequestedProduct));
    }
  }

  async getRequestedProducts(): Promise<ListRequestedProductsResponse> {
    this.loading = true;

    try {
      var request = this.buildListRequest();

      var response = await this.situViewClient.listRequestedProducts(request);

      return response;
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }

    return new ListRequestedProductsResponse();
  }

  async loadFilterOptions(request: GetListRequestedProductsFiltersRequest): Promise<GetListRequestedProductsFiltersResponse> {
    this.loadingFilterOption = true;

    try {
      var response = await this.situViewClient.getListRequestedProductsFilters(request);

      return response;
    } catch (error) {
      console.log(error);
    } finally {
      this.loadingFilterOption = false;
    }

    return new GetListRequestedProductsFiltersResponse();
  }

  buildListRequest() : ListRequestedProductsRequest {
    var request = new ListRequestedProductsRequest();
    request.companyId = this.selectedCompany?.id!;

    request.filterImportCompanyId = this.importCompanyIdFilter;
    request.filterImportId = this.importFileIdFilter;
    request.filterSupplierId = this.supplierIdFilter;
    request.filterRequestedProductState = this.productStateFilter;
    request.filterProductClassificationId = this.productClassificationIdFilter;
    request.filterProductTypeId = this.productTypeIdFilter;
    request.filterBrandId = this.brandIdFilter;
    request.searchTerm = this.searchTerm;

    request.orderBy = this.sortBy;
    request.orderDescending = this.sortDescending;
    request.pageNumber = this.pageNumber;
    request.pageSize = this.pageSize;

    request.includeCurrentState = true;
    request.includeBrand = true;
    request.includeRanges = true;
    request.includeFileVaultReferences = true;

    return request;
  }

  buildFilterOptionsRequest() : GetListRequestedProductsFiltersRequest {
    var request = this.buildListRequest();
    return GetListRequestedProductsFiltersRequest.fromJS(request);
  }

  sortData(sort: Sort) {

    console.log(sort);

    if (!sort.active || sort.direction === '') {
      this.sortBy = ListRequestedProductsRequestOrderField.Unset;
      this.loadData(true);
      return;
    }

    this.sortDescending = sort.direction === 'desc';

    switch (sort.active) {
      case 'name':
        this.sortBy = ListRequestedProductsRequestOrderField.Name;
        break;
      case 'supplier':
        this.sortBy = ListRequestedProductsRequestOrderField.Supplier;
        break;
      case 'productCode':
        this.sortBy = ListRequestedProductsRequestOrderField.ProductCode;
        break;
      case 'productType':
        this.sortBy = ListRequestedProductsRequestOrderField.ProductType;
        break;
      case 'brand':
        this.sortBy = ListRequestedProductsRequestOrderField.Brand;
        break;
      case 'qualityLevel':
        this.sortBy = ListRequestedProductsRequestOrderField.QualityLevel;
        break;
      case 'state':
        this.sortBy = ListRequestedProductsRequestOrderField.CurrentWorkflowState;
        break;
      default:
        this.sortBy = ListRequestedProductsRequestOrderField.Unset;
        break;
    }
    
    this.loadData(true);
  }

  isCompanyName(name: string): boolean {
    return this.allCompanies.some(x => x.name === name)
  }

  getProductTypeName(item: RequestedProduct): string | undefined {
    var productType = this.allProductTypes.find(x => x.id === item.productTypeId)
    return productType?.name;
  }

  isProductTypeName(name: string): boolean {
    return this.allProductTypes.some(x => x.name === name)
  }

  getBrandName(brandId: string | undefined): string {
    if (!brandId) return '';
    return this.brandDirectory[brandId] || 'Unknown Brand';
  }

  onImportRequestedProductsClick() {
    document.getElementById("upload-requested-product-file")?.click();
  }

  onFileChosen(event: any) {
    var fileName = event.target.files[0].name;

    if (!fileName) {
      return; // no file was selected
    }

    var fileExt = fileName.split('.').pop();
    if (fileExt !== 'xlsx') {
      this.showMessage("Only Excel spreadsheets are allowed.");
      return;
    }

    this.loading = true;
    let fileParameter: FileParameter = { data: event.target.files[0], fileName: event.target.files[0].name };

    this.situViewClient.importRequestedProduct(this.selectedCompany?.id, fileParameter)
      .then(async (response: ImportRequestedProductsResponse) => {
        if (response.success) {
          this.showMessage("Requested Products file uploaded");
          this.requestedProducts = response.requestedProducts!.map(x => new RequestedProductLineItem(x));

          this.clearAllFilterOptions();
          if (response.import) {
            this.selectImportFileById(response.import.id!);
            this.selectImportCompanyById(response.import.companyId!);
            await this.loadFilterImportFileOptions(true);
            await this.loadFilterImportCompanyOptions();
          }
        } else {
          this.showMessage("There was an issue importing the requested product file.");

          response.errorMessages?.forEach(x => {
            this.bannerService.AddMessage(x, BannerServiceStatus.error, null, true);
          });
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  async confirmImport(confirm: boolean) {
    this.loading = true;

    try{
      var request = new ConfirmRequestedProductsImportRequest();
      request.companyId = this.selectedImportFile!.companyId;
      request.id = this.selectedImportFile!.id;

      request.confirm = confirm;

      var reponse = await this.situViewClient.confirmRequestedProductsImport(request);

      if (!reponse.success) {
          this.showMessage(`Failed to ${confirm ? "Confirm" : "Cancel"} Import.`);
          return;
      }

    } catch (error) {
      console.error(error);
      this.showMessage(`Failed to ${confirm ? "Confirm" : "Cancel"} Import.`);
      return;
    } finally {
      this.loading = false;
    }

    this.showMessage(`Import has been ${confirm ? "Confirmed" : "Canceled"}.`);

    if (confirm) {
      this.selectedImportFile!.confirmed = true;
    } else {
      this.selectedImportFile = undefined;
      this.clearImportFile();
    }

    this.onSearch();
  }

  onLineItemSupplierSelected(event: any, lineItem: RequestedProductLineItem): void {
    var supplierName = event.option.value;
    lineItem.requestedProduct.supplierId = this.allCompanies?.find(x => x.name === supplierName)?.id;
    lineItem.supplierNameFilter = supplierName;
  }

  onLineItemProductTypeSelected(event: any, lineItem: RequestedProductLineItem): void {
    var productTypeName = event.option.value;
    lineItem.requestedProduct.productTypeId = this.allProductTypes?.find(x => x.name === productTypeName)?.id;
    lineItem.productTypeNameFilter = productTypeName;
  }

  displayBrandName = (brandId: string): string => {
    if (!brandId) return '';
    return this.allBrands?.find(x => x.id === brandId)?.name ?? brandId;
  }

  onLineItemBrandSelected(event: any, lineItem: RequestedProductLineItem): void {
    var brandId = event.option.value;
    lineItem.requestedProduct.brandId = brandId;
  }

  hasEditModeLineItems(): boolean {
    if (!this.requestedProducts) {
      return false;
    }

    return this.requestedProducts.some(x => x.editMode);
  }

  countEditModeLineItems(): number {
    var count = 0;
    if (this.requestedProducts) {
      
      for (var requestedProduct of this.requestedProducts) {
        if (requestedProduct.editMode) {
          count++;
        }
      }
    }
    
    return count;
  }

  editSameRequestedSupplierName(requestedSupplierName: string, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.requestedProduct.requestedSupplierName === requestedSupplierName) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameRequestedProductTypeName(requestedProductTypeName: string, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.requestedProduct.requestedProductTypeName === requestedProductTypeName) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameRequestedBrand(requestedBrand: string, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.requestedProduct.requestedBrand === requestedBrand) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameRequestedRange(requestedRangeName: string | null, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (requestedRangeName === null) {
        // Match products with no ranges at all (blank)
        if (!x.requestedProduct.ranges?.length) {
          this.editLineItem(x, enable)
        }
      } else {
        if (x.requestedProduct.ranges?.some(r => r.requestedRangeName === requestedRangeName)) {
          this.editLineItem(x, enable)
        }
      }
    });
  }

  editSameRequestedFileVaultFiles(requestedFileVaultFile: string | null, category: FileVaultCategory | null, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (requestedFileVaultFile === null && category === null) {
        if (!x.requestedProduct.fileVaultReferences?.length) {
          this.editLineItem(x, enable)
        }
      } else {
        if (x.requestedProduct.fileVaultReferences?.some(f => f.requestedFileVaultFile === requestedFileVaultFile && f.category === category)) {
          this.editLineItem(x, enable)
        }
      }
    });
  }

  editSameRequestedQualityLevel(requestedQualityLevel: string, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if ((x.requestedProduct.requestedQualityLevel ?? null) === (requestedQualityLevel ?? null)) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameProductWorkflowState(productState: ProductWorkflowState, enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.requestedProduct.currentWorkflowState!.state === productState) {
        this.editLineItem(x, enable)
      }
    });
  }

  editAllLineItems(enable: boolean) {
    this.requestedProducts.forEach(x => {
      if (x.editMode !== enable) {
        this.editLineItem(x, enable);
      }
    });
  }

  editLineItem(item: RequestedProductLineItem, enable: boolean) {
    if (enable) {
      this.enableEditMode(item);
    } else {
      this.cancelEditMode(item);
    }
  }

  enableEditMode(item: RequestedProductLineItem) {
    item.editMode = true;
    item.originalRequestedProduct = new RequestedProduct(item.requestedProduct);

    item.filteredSuppliers = item.supplierControl.valueChanges.pipe(
      startWith(item.supplierControl.value),
      map((value) => this.filterBulkSuppliers(value))
    );

    item.filteredProductTypes = item.productTypeControl.valueChanges.pipe(
      startWith(item.productTypeControl.value),
      map((value) => this.filterBulkProductTypes(value))
    );

    item.filteredBrands = item.brandControl.valueChanges.pipe(
      startWith(item.brandControl.value),
      map((value) => this.filterBulkBrands(value, item.requestedProduct.supplierId))
    );

    item.brandControl.setValue(this.brandDirectory[item.requestedProduct.brandId || '']);
    item.brandControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    if (!item.requestedProduct.ranges) {
      item.requestedProduct.ranges = [];
    }
    if (!item.requestedProduct.fileVaultReferences) {
      item.requestedProduct.fileVaultReferences = [];
    }

    item.supplierNameFilter = this.companyDirectory[item.requestedProduct.supplierId || ''];
    item.supplierControl.setValue(item.supplierNameFilter);
    item.supplierControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    item.productTypeNameFilter = this.allProductTypes?.find(x => x.id === item.requestedProduct.productTypeId)?.name;
    item.productTypeControl.setValue(item.productTypeNameFilter);
    item.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  cancelEditMode(item: RequestedProductLineItem) {
    item.editMode = false;
    if (item.originalRequestedProduct) {
      item.requestedProduct = item.originalRequestedProduct;
      item.originalRequestedProduct = undefined;
    }
  }

  searchTermChanged() {
    this.clearAllFilterOptions();
  }

  clearSearchTerm() {
    this.searchTerm = '';
    this.clearAllFilterOptions();
  }

  clearAllFilterOptions(optionsToKeep?: any[]) {
    if (optionsToKeep != this.supplierFilterOptions) {
      this.supplierFilterOptions = undefined;
    } 
    if (optionsToKeep != this.brandFilterOptions) {
      this.brandFilterOptions = undefined;
    }
    if (optionsToKeep != this.importCompanyFilterOptions) {
      this.importCompanyFilterOptions = undefined;
    }
    if (optionsToKeep != this.importFileFilterOptions) {
      this.importFileFilterOptions = undefined;
    }
    if (optionsToKeep != this.productClassificationFilterOptions) {
      this.productClassificationFilterOptions = undefined;
    }
    if (optionsToKeep != this.productTypeFilterOptions) {
      this.productTypeFilterOptions = undefined;
    }
    if (optionsToKeep != this.productStateFilterOptions) {
      this.productStateFilterOptions = undefined;
    }
  }

  //#region Supplier AutoComplete Filter

  supplierControl = new FormControl();
  filteredSuppliers: Observable<CompanyFilterOption[]> | undefined;
  supplierFilterOptions?: CompanyFilterOption[];

  async loadFilterSupplierOptions() {
    if (this.loadingFilterOption || this.supplierFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterSupplierOptions = true;
    var response = await this.loadFilterOptions(request);

    this.supplierFilterOptions = response.filterSupplierOptions;
    this.supplierNameFilter = this.supplierFilterOptions?.find(x => x.id === this.supplierIdFilter)?.name;
    this.supplierControl.setValue(this.supplierNameFilter);
    this.supplierControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onSupplierSelected(event: any): void {
    this.selectSupplierByName(event.option.value);

    this.clearAllFilterOptions(this.supplierFilterOptions);
  }

  selectSupplierByName(supplierName: string) {
    this.supplierIdFilter = this.supplierFilterOptions?.find(x => x.name === supplierName)?.id;
    this.supplierNameFilter = supplierName;
  }

  onClearSupplier() {
    this.clearSupplier();
  }

  clearSupplier() {
    if (this.supplierNameFilter) {
      this.clearAllFilterOptions();
    }

    this.supplierControl.reset();
    this.supplierIdFilter = undefined;
    this.supplierNameFilter = undefined;
  }

  private filterSuppliers(searchTerm: string | undefined = undefined): CompanyFilterOption[] {
    var companies = this.supplierFilterOptions;

    if (!companies) {
      return [];
    }

    // Filter Company Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      companies = companies.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return companies;
  }

  //#endregion Supplier AutoComplete Filter

  //#region Brand AutoComplete Filter

  brandControl = new FormControl();
  filteredBrands: Observable<BrandFilterOption[]> | undefined;
  brandFilterOptions?: BrandFilterOption[];

  async loadFilterBrandOptions() {
    if (this.loadingFilterOption || this.brandFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterBrandOptions = true;
    var response = await this.loadFilterOptions(request);

    this.brandFilterOptions = response.filterBrandOptions;
    this.brandNameFilter = this.brandFilterOptions?.find(x => x.id === this.brandIdFilter)?.name;
    this.brandControl.setValue(this.brandIdFilter);
    this.brandControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onBrandSelected(event: any): void {
    this.selectBrandById(event.option.value);

    this.clearAllFilterOptions(this.brandFilterOptions);
  }

  displayBrandFilterName = (brandId: string): string => {
    if (!brandId) return '';
    return this.brandFilterOptions?.find(x => x.id === brandId)?.name ?? brandId;
  }

  selectBrandByName(brandName: string) {
    this.brandIdFilter = this.brandFilterOptions?.find(x => x.name === brandName)?.id;
    this.brandNameFilter = brandName;
  }

  selectBrandById(brandId: string) {
    this.brandIdFilter = brandId;
    this.brandNameFilter = this.brandFilterOptions?.find(x => x.id === brandId)?.name;
  }

  onClearBrand() {
    this.clearBrand();
  }

  clearBrand() {
    if (this.brandNameFilter) {
      this.clearAllFilterOptions();
    }

    this.brandControl.reset();
    this.brandIdFilter = undefined;
    this.brandNameFilter = undefined;
  }

  private filterBrands(searchTerm: string | undefined = undefined): BrandFilterOption[] {
    var brands = this.brandFilterOptions;

    if (!brands) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      brands = brands.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return brands;
  }

  //#endregion Brand AutoComplete Filter

  //#region Import Company AutoComplete Filter

  importCompanyControl = new FormControl();
  filteredImportCompanies: Observable<CompanyFilterOption[]> | undefined;
  importCompanyFilterOptions?: CompanyFilterOption[];

  async loadFilterImportCompanyOptions() {
    if (this.loadingFilterOption || this.importCompanyFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterImportCompanyOptions = true;
    var response = await this.loadFilterOptions(request);

    this.importCompanyFilterOptions = response.filterImportCompanyOptions;
    this.importCompanyNameFilter = this.importCompanyFilterOptions?.find(x => x.id === this.importCompanyIdFilter)?.name;
    this.importCompanyControl.setValue(this.importCompanyNameFilter);
    this.importCompanyControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onImportCompanySelected(event: any): void {
    this.selectImportCompanyByName(event.option.value);

    this.clearAllFilterOptions(this.importCompanyFilterOptions);
  }

  selectImportCompanyByName(importCompanyName: string) {
    this.importCompanyIdFilter = this.importCompanyFilterOptions?.find(x => x.name === importCompanyName)?.id;
    this.importCompanyNameFilter = importCompanyName;
  }

  selectImportCompanyById(importCompanyId: string) {
    this.importCompanyIdFilter = importCompanyId;
    this.importCompanyNameFilter = this.importCompanyFilterOptions?.find(x => x.id === importCompanyId)?.name;
  }

  onClearImportCompany() {
    this.clearImportCompany();
  }

  clearImportCompany() {
    if (this.importCompanyNameFilter) {
      this.clearAllFilterOptions();
    }

    this.importCompanyControl.reset();
    this.importCompanyIdFilter = undefined;
    this.importCompanyNameFilter = undefined;
  }

  private filterImportCompanies(searchTerm: string | undefined = undefined): CompanyFilterOption[] {
    var importCompanies = this.importCompanyFilterOptions;

    if (!importCompanies) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      importCompanies = importCompanies.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return importCompanies;
  }

  //#endregion Import File AutoComplete Filter

  //#region Import File AutoComplete Filter

  importFileControl = new FormControl();
  filteredImportFiles: Observable<ImportFileFilterOption[]> | undefined;
  importFileFilterOptions?: ImportFileFilterOption[];

  selectedImportFile?: ImportFileFilterOption;

  async loadFilterImportFileOptions(selectImportFile = false) {
    if (this.loadingFilterOption || this.importFileFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterImportFileOptions = true;
    var response = await this.loadFilterOptions(request);

    this.importFileFilterOptions = response.filterImportFileOptions;
    var fileImport = this.importFileFilterOptions?.find(x => x.id === this.importFileIdFilter);
    this.importFileNameFilter = fileImport?.name;
    this.importFileControl.setValue(this.importFileIdFilter);
    this.importFileControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    if (selectImportFile) {
      this.selectedImportFile = fileImport;
    }
  }

  onImportFileSelected(event: any): void {
    this.selectImportFileById(event.option.value);

    this.clearAllFilterOptions(this.importFileFilterOptions);
  }

  displayImportFileName = (importFileId: string): string => {
    if (!importFileId) return '';
    return this.importFileFilterOptions?.find(x => x.id === importFileId)?.name ?? importFileId;
  }

  selectImportFileByName(importFileName: string) {
    this.importFileIdFilter = this.importFileFilterOptions?.find(x => x.name === importFileName)?.id;
    this.importFileNameFilter = importFileName;
  }

  selectImportFileById(importFileId: string) {
    this.importFileIdFilter = importFileId;
    this.importFileNameFilter = this.importFileFilterOptions?.find(x => x.id === importFileId)?.name;
  }

  onClearImportFile() {
    this.clearImportFile();
  }

  clearImportFile() {
    if (this.importFileNameFilter) {
      this.clearAllFilterOptions();
    }

    this.importFileControl.reset();
    this.importFileIdFilter = undefined;
    this.importFileNameFilter = undefined;
  }

  private filterImportFiles(searchTerm: string | undefined = undefined): ImportFileFilterOption[] {
    var importFiles = this.importFileFilterOptions;

    if (!importFiles) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      importFiles = importFiles.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return importFiles;
  }

  importRequestedProductCount(importFile: ImportFileFilterOption) : number {
    return importFile.requestedProductCount!.reduce((sum, current) => sum + current.count!, 0)
  }

  //#endregion Import File AutoComplete Filter

  //#region Product Classification AutoComplete Filter

  productClassificationControl = new FormControl();
  filteredProductClassifications: Observable<ProductClassificationFilterOption[]> | undefined;
  productClassificationFilterOptions?: ProductClassificationFilterOption[];

  async loadFilterProductClassificationOptions() {
    if (this.loadingFilterOption || this.productClassificationFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterProductClassificationOptions = true;
    var response = await this.loadFilterOptions(request);

    this.productClassificationFilterOptions = response.filterProductClassificationOptions;
    this.productClassificationNameFilter = this.productClassificationFilterOptions?.find(x => x.id === this.productClassificationIdFilter)?.name;
    this.productClassificationControl.setValue(this.productClassificationNameFilter);
    this.productClassificationControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }
  
  onProductClassificationSelected(event: any): void {
    this.selectProductClassificationByName(event.option.value);

    this.clearAllFilterOptions(this.productClassificationFilterOptions);
  }

  selectProductClassificationByName(productClassificationName: string) {
    this.productClassificationIdFilter = this.productClassificationFilterOptions?.find(x => x.name === productClassificationName)?.id;
    this.productClassificationNameFilter = productClassificationName;
  }

  selectProductClassificationById(productClassificationId: string) {
    this.productClassificationIdFilter = productClassificationId;
    this.productClassificationNameFilter = this.productClassificationFilterOptions?.find(x => x.id === productClassificationId)?.name;
  }

  onClearProductClassification() {
    this.clearProductClassification();
  }

  clearProductClassification() {
    if (this.productClassificationNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productClassificationControl.reset();
    this.productClassificationIdFilter = undefined;
    this.productClassificationNameFilter = undefined;
  }

  private filterProductClassifications(searchTerm: string | undefined = undefined): ProductClassificationFilterOption[] {
    var productClassifications = this.productClassificationFilterOptions;

    if (!productClassifications) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productClassifications = productClassifications.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return productClassifications;
  }

  //#endregion Product Classification AutoComplete Filter

  //#region Product Type AutoComplete Filter

  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductTypeFilterOption[]> | undefined;
  productTypeFilterOptions?: ProductTypeFilterOption[];

  async loadFilterProductTypeOptions() {
    if (this.loadingFilterOption || this.productTypeFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterProductTypeOptions = true;
    var response = await this.loadFilterOptions(request);

    this.productTypeFilterOptions = response.filterProductTypeOptions;
    this.productTypeNameFilter = this.productTypeFilterOptions?.find(x => x.id === this.productTypeIdFilter)?.name;
    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onProductTypeSelected(event: any): void {
    this.selectProductTypeByName(event.option.value);

    this.clearAllFilterOptions(this.productTypeFilterOptions);
  }

  selectProductTypeByName(productTypeName: string) {
    this.productTypeIdFilter = this.productTypeFilterOptions?.find(x => x.name === productTypeName)?.id;
    this.productTypeNameFilter = productTypeName;
  }

  selectProductTypeById(productTypeId: string) {
    this.productTypeIdFilter = productTypeId;
    this.productTypeNameFilter = this.productTypeFilterOptions?.find(x => x.id === productTypeId)?.name;
  }

  onClearProductType() {
    this.clearProductType();
  }

  clearProductType() {
    if (this.productTypeNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productTypeControl.reset();
    this.productTypeIdFilter = undefined;
    this.productTypeNameFilter = undefined;
  }

  private filterProductTypes(searchTerm: string | undefined = undefined): ProductTypeFilterOption[] {
    var productTypes = this.productTypeFilterOptions;

    if (!productTypes) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productTypes = productTypes.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return productTypes;
  }

  //#endregion Product Type AutoComplete Filter

  //#region Product State AutoComplete Filter

  productStateControl = new FormControl();
  filteredProductStates: Observable<ProductStateFilterOption[]> | undefined;
  productStateFilterOptions?: ProductStateFilterOption[];

  async loadFilterProductStateOptions() {
    if (this.loadingFilterOption || this.productStateFilterOptions) {
      return;
    }

    var request = this.buildFilterOptionsRequest();
    request.includeFilterProductStateOptions = true;
    var response = await this.loadFilterOptions(request);

    this.productStateFilterOptions = response.filterProductStateOptions;
    this.productStateNameFilter = this.productStateFilterOptions?.find(x => x.state === this.productStateFilter)?.name;
    this.productStateControl.setValue(this.productStateNameFilter);
    this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onProductStateSelected(event: any): void {
    this.selectProductStateByName(event.option.value);

    this.clearAllFilterOptions(this.productStateFilterOptions);
  }

  selectProductState(productState: ProductWorkflowState) {
    this.productStateFilter = productState;
    this.productStateNameFilter = this.productStateFilterOptions?.find(x => x.state === productState)?.name;
  }

  selectProductStateByName(productStateName: string) {
    this.productStateFilter = this.productStateFilterOptions?.find(x => x.name === productStateName)?.state;
    this.productStateNameFilter = productStateName;
  }

  onClearProductState() {
    this.clearProductState();
  }

  clearProductState() {
    if (this.productStateNameFilter) {
      this.clearAllFilterOptions();
    }

    this.productStateControl.reset();
    this.productStateFilter = undefined;
    this.productStateNameFilter = undefined;
  }

  private filterProductStates(searchTerm: string | undefined = undefined): ProductStateFilterOption[] {
    var productStates = this.productStateFilterOptions;

    if (!productStates) {
      return [];
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productStates = productStates.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return productStates;
  }

  //#endregion Product State AutoComplete Filter

  //#region Bulk Supplier AutoComplete Filter

  bulkSupplierControl = new FormControl();
  filteredBulkSuppliers: Observable<Company[]> | undefined;

  allCompanies: Company[] = [];
  companyDirectory: Record<string, string> = {};

  private async loadSuppliers() {
    var companies = await this.companyViewService.LoadCompanies();

    this.allCompanies = companies;
    companies.forEach(c => this.companyDirectory[c.id!] = c.name!);

    this.supplierNameFilter = this.allCompanies?.find(x => x.id === this.supplierIdFilter)?.name;
    this.supplierControl.setValue(this.supplierNameFilter);
    this.supplierControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onBulkSupplierSelected(event: any) {
    var supplierName = event.option.value;
    var supplierId = this.allCompanies?.find(x => x.name === supplierName)?.id;

    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.supplierNameFilter = supplierName;
        x.requestedProduct.supplierId = supplierId;
        x.supplierControl.setValue(x.supplierNameFilter);
        x.supplierControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      }
    });
  }

  clearBulkSupplier() {
    this.bulkSupplierControl.reset();
  }

  private filterBulkSuppliers(searchTerm: string | undefined = undefined): Company[] {
    var companies = this.allCompanies;

    // Filter Company Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      companies = companies.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return companies;
  }

  //#endregion Bulk Supplier AutoComplete Filter

  //#region Bulk Product Type AutoComplete Filter

  bulkProductTypeControl = new FormControl();
  filteredBulkProductTypes: Observable<ProductType[]> | undefined;
  allProductTypes: ProductType[] = [];

  private async loadProductTypes() {
    var productTypes = await this.productService.LoadProductTypes();

    this.allProductTypes = productTypes;

    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === this.productTypeIdFilter)?.name;
    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onBulkProductTypeSelected(event: any) {
    var productTypeName = event.option.value;
    var productTypeId = this.allProductTypes?.find(x => x.name === productTypeName)?.id;

    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.productTypeNameFilter = productTypeName;
        x.requestedProduct.productTypeId = productTypeId;
        x.productTypeControl.setValue(x.productTypeNameFilter);
        x.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      }
    });
  }

  clearBulkProductType() {
    this.bulkProductTypeControl.reset();
  }

  private filterBulkProductTypes(searchTerm: string | undefined = undefined): ProductType[] {
    var productTypes = this.allProductTypes;

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productTypes = productTypes.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return productTypes;
  }

  //#endregion Bulk Product Type AutoComplete Filter

  //#region Bulk Brand AutoComplete Filter

  bulkBrandControl = new FormControl();
  filteredBulkBrands: Observable<Brand[]> | undefined;
  allBrands: Required<Brand>[] = [];
  brandDirectory: Record<string, string> = {};

  private async loadAllBrands() {
    var brandsResponse = await this.situViewClient.listBrands(new ListBrandsRequest());

    let brands = brandsResponse.brands;

    this.allBrands = brands as Required<Brand>[] || [];
    this.allBrands.sort((a, b) => a.name.localeCompare(b.name));

    this.brandDirectory = {};
    this.allBrands.forEach(b => this.brandDirectory[b.id!] = b.name!);
  }

  onBulkBrandSelected(event: any) {
    var brandId = event.option.value;

    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.requestedProduct.brandId = brandId;
        x.brandControl.setValue(brandId);
        x.supplierControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      }
    });
  }

  clearBulkBrand() {
    this.bulkBrandControl.reset();
  }

  onBulkBrandChanged(event: any) {
    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.requestedProduct.brand = event.target.value;
      }
    });
  }

  private filterBulkBrands(searchTerm: string | undefined = undefined, supplierId?: string): Brand[] {
    var brands = this.allBrands;

    if (supplierId) {
      brands = brands.filter(brand => brand.companyId === supplierId);
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      brands = brands.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return brands;
  }

  //#endregion Bulk Brand AutoComplete Filter

  //#region Bulk Workflow

  allProductStates: ProductWorkflow[] = [];

  private async loadProductStates() {
    var productWorkflow = await this.productService.LoadProductWorkflowConfiguration();

    this.allProductStates = productWorkflow;

    this.productStateNameFilter = this.allProductStates?.find(x => x.state === this.productStateFilter)?.name;
    this.productStateControl.setValue(this.productStateNameFilter);
    this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  getWorkflowState(item: RequestedProduct): string | undefined {
    if (!item.currentWorkflowState) {
      return undefined;
    }

    var stateInfo = this.allProductStates.find(x => x.state === item.currentWorkflowState?.state)
    return stateInfo?.name;
  }

  getEditModeWorkflowState(item: RequestedProduct): string | undefined {
    if (!item.currentWorkflowState) {
      return undefined;
    }

    var stateInfo = this.allProductStates.find(x => x.state === item.currentWorkflowState?.state)
    return stateInfo?.name;
  }

  onBulkOpenWorkflowDialog() {

    var processResult = async (result: WorkflowTriggerResultData) => {

      var count = 0;
      var errorCount = 0;

      for (var lineItem of this.requestedProducts) {

        if (lineItem.editMode) {
          var requestedProduct = lineItem.requestedProduct;
          var triggerResult = await this.productService.TriggerRequestedProductWorkflow(requestedProduct.id!, result.selectedTransition?.trigger, result.comments);
          if (lineItem.originalRequestedProduct) {
            lineItem.originalRequestedProduct.currentWorkflowState = triggerResult;
          }

          count++;
          if (triggerResult) {
            requestedProduct.currentWorkflowState = triggerResult;
          } else {
            errorCount++;
          }

          this.showMessage(`Workflow transitioned ${count} requested products with ${errorCount} errors!`);
        }
      }
    }

    // It should not be possible to get here unless all the workflow states are the same
    var workflowState = this.workflowStateOfRequestedProductsInEditModeAreTheSame();
    this.openWorkflowDialog(workflowState!, undefined, processResult);

  }

  onOpenWorkflowDialog(item: RequestedProductLineItem) {

    var requestedProduct = item.requestedProduct;
    var processResult = async (result: WorkflowTriggerResultData) => {
      var triggerResult = await this.productService.TriggerRequestedProductWorkflow(requestedProduct.id!, result.selectedTransition?.trigger, result.comments);

      if (triggerResult) {
        var stateHasChanged = requestedProduct.currentWorkflowState?.state !== triggerResult?.state;
        requestedProduct.currentWorkflowState = triggerResult;
        if (item.originalRequestedProduct) {
          item.originalRequestedProduct.currentWorkflowState = triggerResult;
        }

        if (stateHasChanged) {
          this.showMessage(`Workflow successfully transitioned to ${triggerResult.name}!`);
        } else {
          this.showMessage(`Comment successfully added!`);
        }
      } else {
        this.showMessage("Failed to update workflow!");
      }

    }

    var preWorkflowTriggerSteps: PreWorkflowTriggerStep[] = [];

    // The the Requested product is marked as a duplicate, allow the user to select the product
    var setDuplicateProductId = {
      requiredProperty: "DuplicateProductId",
      preTriggerFunction: async () => { return await this.openProductSelector(item); }
    } as PreWorkflowTriggerStep;
    preWorkflowTriggerSteps.push(setDuplicateProductId);

    this.openWorkflowDialog(requestedProduct.currentWorkflowState!, requestedProduct, processResult, preWorkflowTriggerSteps);
  }

  openWorkflowDialog(workflowState: ProductWorkflow, workflowSubject: any | undefined,
    processResult: (result: WorkflowTriggerResultData) => void,
    preWorkflowTriggerSteps: PreWorkflowTriggerStep[] | undefined = undefined) {
    const dialogWidth = '800px';
    const dialogHeight = '500px';

    this.workflowTriggerDialogRef = this.dialog.open(WorkflowTriggerDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: {
        workflowSubject: workflowSubject,
        workflowState: workflowState,
        preWorkflowTriggerSteps: preWorkflowTriggerSteps,
      }
    });

    this._subscriptions.push(this.workflowTriggerDialogRef.afterClosed()
      .subscribe((data: WorkflowTriggerResultData) => {
        if (!data) {
          // Dialog was closed without triggering workflow
          return;
        }

        processResult(data);
      })
    );
  }

  async openProductSelector(item: RequestedProductLineItem): Promise<boolean> {

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    var dialogData = new ProductSelectorDialogData();
    dialogData.companyId = this.selectedCompany?.id;
    dialogData.multiSelect = false;
    dialogData.tagSelectionEnabled = false;

    this.productSelectorDialogRef = this.dialog.open(ProductSelectorDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: dialogData,
    });

    var result: ProductSelectorResultData = await this.productSelectorDialogRef.afterClosed().toPromise();

    if (!result?.products[0]) {
      return false;
    }

    item.requestedProduct.duplicateProductId = result.products[0].id;

    await this.saveLineItem(item);

    return true;
  }

  workflowStateOfRequestedProductsInEditModeAreTheSame(): ProductWorkflow | undefined {
    // Filter the objects to find the selected ones
    const editModeProducts = this.requestedProducts.filter(x => x.editMode);

    // Check if there are no selected objects
    if (editModeProducts.length === 0) {
      return undefined;
    }

    var firstWorkflowState = editModeProducts[0].requestedProduct.currentWorkflowState;
    // Check if all workflow states are the same
    const allSame = editModeProducts.every(x => x.requestedProduct.currentWorkflowState!.state === firstWorkflowState!.state);

    return allSame ? firstWorkflowState : undefined;
  }

  //#endregion Bulk Workflow

  //#region Ranges

  rangesBySupplier: Record<string, Range[]> = {};

  async loadRangesForSupplier(supplierId: string): Promise<Range[]> {
    if (this.rangesBySupplier[supplierId]) {
      return this.rangesBySupplier[supplierId];
    }

    var request = new ListRangesRequest();
    request.companyId = supplierId;
    request.pageSize = 9999;
    request.pageNumber = 1;

    var response = await this.rangeService.ListRanges(request);
    var ranges = response.items || [];
    this.rangesBySupplier[supplierId] = ranges;
    return ranges;
  }

  getRangesForSupplier(supplierId?: string): Range[] {
    if (!supplierId) return [];
    return this.rangesBySupplier[supplierId] || [];
  }

  filterRangesForSupplier(searchTerm?: string, supplierId?: string, excludeRangeIds?: string[]): Range[] {
    var ranges = this.getRangesForSupplier(supplierId);

    if (excludeRangeIds?.length) {
      ranges = ranges.filter(r => !excludeRangeIds.includes(r.id!));
    }

    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      ranges = ranges.filter(r => r.name && r.name.toLowerCase().includes(searchTerm!));
    }

    return ranges;
  }

  rangePickerControl = new FormControl();
  rangePickerTarget: { rangeRow?: RequestedProductRange, lineItem?: RequestedProductLineItem, mode: 'set' | 'add' } | null = null;

  openRangePicker(lineItem: RequestedProductLineItem, rangeRow?: RequestedProductRange) {
    this.rangePickerTarget = { rangeRow, lineItem, mode: rangeRow ? 'set' as const : 'add' as const };
    this.rangePickerControl.setValue('');

    if (lineItem.requestedProduct.supplierId) {
      this.loadRangesForSupplier(lineItem.requestedProduct.supplierId);
    }
  }

  onRangePickerSelected(event: any) {
    var rangeName = event.option.value;
    if (!this.rangePickerTarget) return;

    var supplierId = this.rangePickerTarget.lineItem?.requestedProduct.supplierId;
    var range = this.getRangesForSupplier(supplierId).find(x => x.name === rangeName);
    if (!range) return;

    if (this.rangePickerTarget.mode === 'set' && this.rangePickerTarget.rangeRow) {
      this.rangePickerTarget.rangeRow.range = range;
    } else if (this.rangePickerTarget.mode === 'add' && this.rangePickerTarget.lineItem) {
      this.onAddManualRange(this.rangePickerTarget.lineItem, range);
    }

    this.rangePickerTarget = null;
    this.rangePickerControl.setValue('');
  }

  closeRangePicker() {
    this.rangePickerTarget = null;
    this.rangePickerControl.setValue('');
  }

  isRangePickerOpen(lineItem: RequestedProductLineItem, rangeRow?: RequestedProductRange): boolean {
    if (!this.rangePickerTarget) return false;
    if (rangeRow) return this.rangePickerTarget.rangeRow === rangeRow;
    return this.rangePickerTarget.lineItem === lineItem && this.rangePickerTarget.mode === 'add';
  }

  getImportRanges(item: RequestedProduct): RequestedProductRange[] {
    return item.ranges?.filter(r => r.requestedRangeName != null) || [];
  }

  getManualRanges(item: RequestedProduct): RequestedProductRange[] {
    return item.ranges?.filter(r => r.requestedRangeName == null && r.range != null) || [];
  }

  getRangeDisplaySummary(item: RequestedProduct): string {
    if (!item.ranges?.length) return '';
    return item.ranges
      .filter(r => r.range != null)
      .map(r => r.range!.name)
      .join(', ');
  }

  onClearRange(rangeRow: RequestedProductRange, item: RequestedProductLineItem) {
    if (rangeRow.requestedRangeName != null) {
      rangeRow.range = undefined;
    } else {
      var index = item.requestedProduct.ranges!.indexOf(rangeRow);
      if (index >= 0) {
        item.requestedProduct.ranges!.splice(index, 1);
      }
    }
  }

  onAddManualRange(item: RequestedProductLineItem, selectedRange: Range) {
    if (!item.requestedProduct.ranges) {
      item.requestedProduct.ranges = [];
    }
    if (item.requestedProduct.ranges.some(r => r.range?.id === selectedRange.id)) return;

    var newRow = new RequestedProductRange();
    newRow.range = selectedRange;
    item.requestedProduct.ranges.push(newRow);
  }

  buildRangeIds(item: RequestedProduct): string[] {
    return (item.ranges || [])
      .filter(r => r.range?.id)
      .map(r => r.range!.id!);
  }

  getEditModeCommonSupplierId(): string | undefined {
    var editModeItems = this.requestedProducts.filter(x => x.editMode);
    if (!editModeItems.length) return undefined;

    var firstSupplierId = editModeItems[0].requestedProduct.supplierId;
    if (!firstSupplierId) return undefined;

    var allSame = editModeItems.every(x => x.requestedProduct.supplierId === firstSupplierId);
    return allSame ? firstSupplierId : undefined;
  }

  bulkRangeControl = new FormControl();
  filteredBulkRanges: Observable<Range[]> | undefined;

  onBulkRangeSelected(event: any) {
    var rangeName = event.option.value;
    var supplierId = this.getEditModeCommonSupplierId();
    var range = this.getRangesForSupplier(supplierId).find(x => x.name === rangeName);
    if (!range) return;

    this.onBulkAddRange(range);
    this.bulkRangeControl.setValue('');
  }

  async onBulkRangePickerOpened() {
    this.bulkRangeControl.setValue('');
    var supplierId = this.getEditModeCommonSupplierId();
    if (supplierId) {
      await this.loadRangesForSupplier(supplierId);
      this.bulkRangeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }
  }

  onBulkAddRange(selectedRange: Range) {
    this.requestedProducts.forEach(x => {
      if (x.editMode) {
        this.onAddManualRange(x, selectedRange);
      }
    });
  }

  onBulkClearRanges() {
    this.requestedProducts.forEach(x => {
      if (x.editMode && x.requestedProduct.ranges) {
        x.requestedProduct.ranges.forEach(r => {
          if (r.requestedRangeName != null) {
            r.range = undefined;
          }
        });
        x.requestedProduct.ranges = x.requestedProduct.ranges.filter(r => r.requestedRangeName != null);
      }
    });
  }

  //#endregion Ranges

  //#region File Vault Files

  public readonly FileVaultCategory = FileVaultCategory;

  getFileCategories(item: RequestedProduct): FileVaultCategory[] {
    if (!item.fileVaultReferences?.length) return [];
    var categories = new Set<FileVaultCategory>();
    item.fileVaultReferences.forEach(f => {
      if (f.category != null) categories.add(f.category);
    });
    return Array.from(categories);
  }

  getImportFilesByCategory(item: RequestedProduct, category: FileVaultCategory): RequestedProductFileVaultReference[] {
    return item.fileVaultReferences?.filter(f => f.category === category && f.requestedFileVaultFile != null) || [];
  }

  getManualFilesByCategory(item: RequestedProduct, category: FileVaultCategory): RequestedProductFileVaultReference[] {
    return item.fileVaultReferences?.filter(f => f.category === category && f.requestedFileVaultFile == null && f.fileVaultReference != null) || [];
  }

  getCategoryLabel(category: FileVaultCategory): string {
    switch (category) {
      case FileVaultCategory.ReferenceMaterial: return 'Reference Material';
      case FileVaultCategory.SourceMaterial: return 'Source Material';
      case FileVaultCategory.Assets: return 'Assets';
      case FileVaultCategory.MarketingMaterial: return 'Marketing Material';
      case FileVaultCategory.Logo: return 'Logo';
      default: return 'Other';
    }
  }

  onSetFile(fileRow: RequestedProductFileVaultReference, selectedFile: FileVaultReference) {
    fileRow.fileVaultReference = selectedFile;
  }

  onClearFile(fileRow: RequestedProductFileVaultReference, item: RequestedProductLineItem) {
    if (fileRow.requestedFileVaultFile != null) {
      fileRow.fileVaultReference = undefined;
    } else {
      var index = item.requestedProduct.fileVaultReferences!.indexOf(fileRow);
      if (index >= 0) {
        item.requestedProduct.fileVaultReferences!.splice(index, 1);
      }
    }
  }

  openFileVaultSelectorAndApply(item: RequestedProductLineItem, category: FileVaultCategory | undefined, callback: (files: FileVaultReference[], category?: FileVaultCategory) => void) {
    var dialogData = new FileVaultSelectorDialogData();
    dialogData.companyId = item.requestedProduct.supplierId!;
    dialogData.fileVaultCategory = category;
    dialogData.validFileExtensions = [];

    var dialogRef = this.dialog.open(FileVaultSelectorDialogComponent, {
      width: '800px',
      height: '600px',
      data: dialogData,
    });

    this._subscriptions.push(dialogRef.afterClosed()
      .subscribe((result: FileVaultSelectorResultData) => {
        if (result?.fileVaultReferences) {
          callback(result.fileVaultReferences, result.category);
        }
      })
    );
  }

  onSwapFile(fileRow: RequestedProductFileVaultReference, item: RequestedProductLineItem) {
    this.openFileVaultSelectorAndApply(item, fileRow.category!, (files) => {
      if (files.length > 0) {
        fileRow.fileVaultReference = files[0];
      }
    });
  }

  onAddFileToUnmatched(fileRow: RequestedProductFileVaultReference, item: RequestedProductLineItem) {
    this.openFileVaultSelectorAndApply(item, fileRow.category!, (files) => {
      if (files.length > 0) {
        fileRow.fileVaultReference = files[0];
      }
    });
  }

  onAddManualFile(item: RequestedProductLineItem) {
    this.openFileVaultSelectorAndApply(item, undefined, (files, category) => {
      if (!item.requestedProduct.fileVaultReferences) {
        item.requestedProduct.fileVaultReferences = [];
      }
      files.forEach(f => {
        if (!item.requestedProduct.fileVaultReferences!.some(existing => existing.fileVaultReference?.id === f.id)) {
          var newRow = new RequestedProductFileVaultReference();
          newRow.fileVaultReference = f;
          newRow.category = category;
          item.requestedProduct.fileVaultReferences!.push(newRow);
        }
      });
    });
  }

  buildFileVaultReferenceIds(item: RequestedProduct): string[] {
    return (item.fileVaultReferences || [])
      .filter(f => f.fileVaultReference?.id)
      .map(f => f.fileVaultReference!.id!);
  }

  onBulkAddFileVault() {
    var supplierId = this.getEditModeCommonSupplierId();
    if (!supplierId) return;

    var dialogData = new FileVaultSelectorDialogData();
    dialogData.companyId = supplierId;
    dialogData.validFileExtensions = [];

    var dialogRef = this.dialog.open(FileVaultSelectorDialogComponent, {
      width: '800px',
      height: '600px',
      data: dialogData,
    });

    this._subscriptions.push(dialogRef.afterClosed()
      .subscribe((result: FileVaultSelectorResultData) => {
        if (!result?.fileVaultReferences) return;

        this.requestedProducts.forEach(x => {
          if (x.editMode) {
            if (!x.requestedProduct.fileVaultReferences) {
              x.requestedProduct.fileVaultReferences = [];
            }
            result.fileVaultReferences.forEach(f => {
              if (!x.requestedProduct.fileVaultReferences!.some(existing => existing.fileVaultReference?.id === f.id)) {
                var newRow = new RequestedProductFileVaultReference();
                newRow.fileVaultReference = f;
                newRow.category = result.category;
                x.requestedProduct.fileVaultReferences!.push(newRow);
              }
            });
          }
        });
      })
    );
  }

  onBulkClearFileVault() {
    this.requestedProducts.forEach(x => {
      if (x.editMode && x.requestedProduct.fileVaultReferences) {
        x.requestedProduct.fileVaultReferences.forEach(f => {
          if (f.requestedFileVaultFile != null) {
            f.fileVaultReference = undefined;
          }
        });
        x.requestedProduct.fileVaultReferences = x.requestedProduct.fileVaultReferences.filter(f => f.requestedFileVaultFile != null);
      }
    });
  }

  //#endregion File Vault Files

  //#region Bulk Changed Event

  onBulkQualityLevelChanged(event: any) {
    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        let value = event.target.value
        x.requestedProduct.qualityLevel = value ? Number(event.target.value) : undefined;
      }
    });
  }

  onBulkNotesChanged(event: any) {
    return this.requestedProducts.forEach(x => {
      if (x.editMode) {
        x.requestedProduct.notes = event.target.value;
      }
    });
  }

  //#endregion Bulk Changed Event
  
  isDefaultId(id: string){
    return id === Guid.EMPTY;
  }
  
  timeAgoString(date?: Date): string {
    return Utils.timeAgoString(date);
  }

  pluralize(unit: string, value: number): string {
    return Utils.pluralize(unit, value);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

}

class RequestedProductLineItem {

  requestedProduct: RequestedProduct;
  originalRequestedProduct: RequestedProduct | undefined;

  editMode = false;

  supplierControl = new FormControl();
  filteredSuppliers: Observable<Company[]> | undefined;
  supplierNameFilter: string | undefined;

  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductType[]> | undefined;
  productTypeNameFilter: string | undefined;

  brandControl: FormControl;
  filteredBrands: Observable<Brand[]> | undefined;

  constructor(requestedProduct: RequestedProduct) {
    this.requestedProduct = requestedProduct;
    this.brandControl = new FormControl();
  }

  allowEditMode() {
    var currentState = this.requestedProduct.currentWorkflowState!.state;
    return currentState == ProductWorkflowState.NewRequest || currentState == ProductWorkflowState.OnHold;
  }

  allowWorkflowTransition() {
    return this.requestedProduct.currentWorkflowState!.state != ProductWorkflowState.NewProduct;
  }

  clearSupplier() {
    this.supplierControl.reset();
    this.requestedProduct.supplierId = undefined;
    this.supplierNameFilter = undefined;
  }

  clearProductType() {
    this.productTypeControl.reset();
    this.requestedProduct.productTypeId = undefined;
    this.productTypeNameFilter = undefined;
  }

  clearBrand() {
    this.brandControl.reset();
    this.requestedProduct.brandId = undefined;
  }
}